import React, { useMemo } from 'react';
import {
  Page,
  Text,
  Image,
  View,
  Document,
  StyleSheet,
} from '@react-pdf/renderer';
import { IDocumentDetailField } from '../../../../../common/document/interfaces/document-detail-field';
import { joinAnswers } from './utils';

const styles = StyleSheet.create({
  page: {
    flexDirection: 'column',
    justifyContent: 'space-between',
    alignContent: 'space-between',
    backgroundColor: '#FFF',
    padding: '36px',
    alignItems: 'stretch',
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'stretch',
    backgroundColor: '#FFF',
  },
  contentText: {
    padding: 0,
  },
  title: {
    width: '100%',
    textAlign: 'center',
    textDecoration: 'underline',
    fontWeight: 'bold',
    fontSize: '12px',
    lineHeight: '100%',
    marginBottom: '12px',
  },
  description: {
    width: '100%',
    fontSize: '10px',
    lineHeight: '100%',
    marginBottom: '12px',
  },
  additionalInformation: {
    width: '100%',
    fontSize: '10px',
    lineHeight: '100%',
    marginTop: '18px',
  },
  explain: {
    width: '100%',
    fontSize: '10px',
    lineHeight: '100%',
    marginTop: '12px',
    textDecoration: 'underline',
  },
  table: {
    display: 'flex',
    flexDirection: 'column',
  },
  tableHeadRow: {
    display: 'flex',
    flexDirection: 'row',
    border: '0',
    fontSize: '10px',
    lineHeight: '100%',
  },
  tableCellHeadFirst: {
    padding: '4px 12px',
    width: '100%',
  },
  tableCellHeadLast: {
    padding: '4px 12px',
    width: '50px',
  },
  tableRow: {
    display: 'flex',
    flexDirection: 'row',
    border: '1px solid black',
    borderBottom: '0',
    fontSize: '10px',
    lineHeight: '100%',
  },
  tableRowLast: {
    display: 'flex',
    flexDirection: 'row',
    border: '1px solid black',
    fontSize: '10px',
    lineHeight: '100%',
  },
  tableCellQuestion: {
    padding: '4px 12px',
    width: '100%',
    borderRight: '1px solid black',
    textOverflow: 'ellipsis',
  },
  tableCellSubQuestion: {
    padding: '4px 12px 4px 14px',
    width: '100%',
    borderRight: '1px solid black',
    textOverflow: 'ellipsis',
  },
  tableCellSelect: {
    padding: '4px 0',
    width: '50px',
    textAlign: 'center',
    borderRight: '1px solid black',
  },
  tableCellSelectLast: {
    padding: '4px 0',
    width: '50px',
    textAlign: 'center',
  },
  question: {
    width: '100%',
    fontWeight: 'bold',
    fontSize: '14px',
    textAlign: 'center',
  },
  questionSecond: {
    marginBottom: '8px',
    marginTop: '8px',
  },
  consentText: {
    width: '100%',
    fontSize: '10px',
    lineHeight: '100%',
    marginTop: '10px',
    marginBottom: '10px',
  },
  footerText: {
    width: '50%',
    fontSize: '12px',
    lineHeight: '100%',
    marginTop: '14px',
    display: 'flex',
    flexDirection: 'row',
    gap: '8px',
  },
  footerRow: {
    display: 'flex',
    flexDirection: 'row',
  },
  sectionImage: {
    width: '100%',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: '10px',
  },
  logo: {
    width: '200px',
  },
});

export type TAnswers = {
  drugOrAlcohol: boolean;
  drugOrAlcoholExplanation?: string;
  amphetamines: boolean;
  amphetaminesExplanation?: string;
  opiates: boolean;
  opiatesExplanation?: string;
  marijuana: boolean;
  marijuanaExplanation?: string;
  cocaine: boolean;
  cocaineExplanation?: string;
  pcp: boolean;
  pcpExplanation?: string;
  otherDrugs: boolean;
  otherDrugsExplanation?: string;
  hearing: boolean;
  hearingIsCurrent?: boolean;
  skillPerformanceEvaluation: boolean;
  skillPerformanceEvaluationIsCurrent?: boolean;
  alternativeVisionStandard: boolean;
  alternativeVisionStandardIsCurrent?: boolean;
};

type InputProps = {
  logo: string;
  driverName: string;
  employeeId: string;
  date: string;
  details?: IDocumentDetailField[] | undefined;
};

export const DriverChecklistPdf = ({
  logo,
  driverName,
  employeeId,
  date,
  details,
}: InputProps) => {
  const {
    drugOrAlcohol,
    drugOrAlcoholExplanation = '',
    amphetamines,
    amphetaminesExplanation = '',
    opiates,
    opiatesExplanation = '',
    marijuana,
    marijuanaExplanation = '',
    cocaine,
    cocaineExplanation = '',
    pcp,
    pcpExplanation = '',
    otherDrugs,
    otherDrugsExplanation = '',
    hearing,
    hearingIsCurrent,
    skillPerformanceEvaluation,
    skillPerformanceEvaluationIsCurrent,
    alternativeVisionStandard,
    alternativeVisionStandardIsCurrent,
  } = useMemo(() => {
    if (!details || !details.length) {
      return {} as TAnswers;
    }
    return details?.reduce((hash: any, current) => {
      hash[current.fieldName] = current.fieldValue;
      return hash;
    }, {} as any);
  }, [details]);

  return (
    <Document>
      <Page size="LETTER" style={styles.page}>
        <View style={styles.row}>
          <View style={styles.contentText}>
            <View style={styles.sectionImage}>
              {logo && <Image style={styles.logo} src={logo} />}
            </View>
            <View>
              <Text style={styles.title}>Checklist for Drivers</Text>
            </View>
            <View>
              <Text style={styles.description}>
                The following list will be used by J.B. Hunt Transport, Inc.
                (“Carrier”) to assist in determining that an employee driver or
                a contractor providing services under the Independent Contractor
                Operating Agreement (as applicable) is qualified to safely
                operate a commercial motor vehicle as required by the Federal
                Motor Carrier Safety Regulations under Part 391.
              </Text>
            </View>
            <View>
              <Text style={styles.question}>
                In the past 6 months, have you?
              </Text>
            </View>
            <View style={styles.table}>
              <View style={styles.tableHeadRow}>
                <Text style={styles.tableCellHeadFirst}></Text>
                <Text style={styles.tableCellHeadLast}>Yes</Text>
                <Text style={styles.tableCellHeadLast}>No</Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellQuestion}>
                  1. Received or been recommended for drug or alcohol counseling
                </Text>
                <Text style={styles.tableCellSelect}>
                  {drugOrAlcohol ? 'X' : ''}
                </Text>
                <Text style={styles.tableCellSelectLast}>
                  {drugOrAlcohol ? '' : 'X'}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellQuestion}>
                  2. Used amphetamines/stimulants of any kind
                </Text>
                <Text style={styles.tableCellSelect}>
                  {amphetamines ? 'X' : ''}
                </Text>
                <Text style={styles.tableCellSelectLast}>
                  {amphetamines ? '' : 'X'}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellQuestion}>
                  3. Used opiates/opioids (i.e. hydro, oxy, codeine, morphine,
                  heroin)
                </Text>
                <Text style={styles.tableCellSelect}>{opiates ? 'X' : ''}</Text>
                <Text style={styles.tableCellSelectLast}>
                  {opiates ? '' : 'X'}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellQuestion}>
                  4. Used marijuana, hashish, or any THC
                </Text>
                <Text style={styles.tableCellSelect}>
                  {marijuana ? 'X' : ''}
                </Text>
                <Text style={styles.tableCellSelectLast}>
                  {marijuana ? '' : 'X'}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellQuestion}>5. Used cocaine</Text>
                <Text style={styles.tableCellSelect}>{cocaine ? 'X' : ''}</Text>
                <Text style={styles.tableCellSelectLast}>
                  {cocaine ? '' : 'X'}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellQuestion}>
                  6. Used PCP or another hallucinogen
                </Text>
                <Text style={styles.tableCellSelect}>{pcp ? 'X' : ''}</Text>
                <Text style={styles.tableCellSelectLast}>{pcp ? '' : 'X'}</Text>
              </View>
              <View style={styles.tableRowLast}>
                <Text style={styles.tableCellQuestion}>
                  7. Used or taken any other illegal drug
                </Text>
                <Text style={styles.tableCellSelect}>
                  {otherDrugs ? 'X' : ''}
                </Text>
                <Text style={styles.tableCellSelectLast}>
                  {otherDrugs ? '' : 'X'}
                </Text>
              </View>
            </View>
            <View>
              <Text style={{ ...styles.question, ...styles.questionSecond }}>
                Have you
              </Text>
            </View>
            <View style={styles.table}>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellQuestion}>
                  8. Been issued a waiver or exemption by US DOT for hearing or
                  seizures?
                </Text>
                <Text style={styles.tableCellSelect}>{hearing ? 'X' : ''}</Text>
                <Text style={styles.tableCellSelectLast}>
                  {hearing ? '' : 'X'}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellSubQuestion}>
                  If so, is it current?
                </Text>
                <Text style={styles.tableCellSelect}>
                  {hearing && hearingIsCurrent === true ? 'X' : ''}
                </Text>
                <Text style={styles.tableCellSelectLast}>
                  {hearing && hearingIsCurrent === false ? 'X' : ''}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellQuestion}>
                  9. Obtained a Skill Performance Evaluation (SPE) certification
                  from the US DOT?
                </Text>
                <Text style={styles.tableCellSelect}>
                  {skillPerformanceEvaluation ? 'X' : ''}
                </Text>
                <Text style={styles.tableCellSelectLast}>
                  {skillPerformanceEvaluation ? '' : 'X'}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellSubQuestion}>
                  If so, is it current?
                </Text>
                <Text style={styles.tableCellSelect}>
                  {skillPerformanceEvaluation &&
                  skillPerformanceEvaluationIsCurrent === true
                    ? 'X'
                    : ''}
                </Text>
                <Text style={styles.tableCellSelectLast}>
                  {skillPerformanceEvaluation &&
                  skillPerformanceEvaluationIsCurrent === false
                    ? 'X'
                    : ''}
                </Text>
              </View>
              <View style={styles.tableRow}>
                <Text style={styles.tableCellQuestion}>
                  10. Been sent for an alternative vision standard via the
                  MCSA-5871.
                </Text>
                <Text style={styles.tableCellSelect}>
                  {alternativeVisionStandard ? 'X' : ''}
                </Text>
                <Text style={styles.tableCellSelectLast}>
                  {alternativeVisionStandard ? '' : 'X'}
                </Text>
              </View>
              <View style={styles.tableRowLast}>
                <Text style={styles.tableCellSubQuestion}>
                  Have you had the required road test?
                </Text>
                <Text style={styles.tableCellSelect}>
                  {alternativeVisionStandard &&
                  alternativeVisionStandardIsCurrent === true
                    ? 'X'
                    : ''}
                </Text>
                <Text style={styles.tableCellSelectLast}>
                  {alternativeVisionStandard &&
                  alternativeVisionStandardIsCurrent === false
                    ? 'X'
                    : ''}
                </Text>
              </View>
            </View>
            <View>
              <Text style={styles.additionalInformation}>
                Explain any yes answer below (When –{' '}
                <Text style={{ fontWeight: 700 }}>Specific Date</Text>, Specific
                Substance, How Long, How Treated, etc.):
              </Text>
              <Text style={styles.explain}>
                {joinAnswers({
                  drugOrAlcoholExplanation,
                  amphetaminesExplanation,
                  opiatesExplanation,
                  marijuanaExplanation,
                  cocaineExplanation,
                  pcpExplanation,
                  otherDrugsExplanation,
                })}
              </Text>
            </View>
            <View>
              <Text style={styles.consentText}>
                I hereby certify that all information on this form is correct
                and complete to the best of my knowledge. I authorize the
                individual or clinic that performed my DOT physical to release
                my DOT Medical Certificate and any other information that may be
                used to determine my ability to safely operate a tractor-trailer
                to Carrier and to any physician Carrier may choose to review my
                job qualifications. I understand that this form and other
                information gathered will be used for determining my ability to
                operate a commercial motor vehicle safely as required by the
                Federal Motor Carrier Safety Act. I understand that any false
                information or misrepresentations made by me will result in
                denial of my driver application or termination of my employment
                or contract with Carrier, as applicable. If I am an employee
                driver, I understand that providing false information or
                misrepresentations will result in loss of any workers’
                compensation benefits to which I might otherwise be entitled (as
                permitted by law) if a condition I fail to report results in an
                injury or the condition is aggravated. I further certify that I
                have received information concerning Medical Qualification
                contained in the Federal Motor Carrier Safety Regulations,
                including Parts 391.41 and 391.47.
              </Text>
            </View>
            <View style={styles.footerRow}>
              <View style={styles.footerText}>
                <Text>Name:</Text>{' '}
                <Text style={{ fontWeight: 500, textDecoration: 'underline' }}>
                  {driverName}
                </Text>
              </View>
              <View style={styles.footerText}>
                <Text>ALPHA Code:</Text>{' '}
                <Text style={{ fontWeight: 500, textDecoration: 'underline' }}>
                  {employeeId}
                </Text>
              </View>
            </View>
            <View style={styles.footerRow}>
              <View style={styles.footerText}>
                <Text>Date:</Text>{' '}
                <Text style={{ fontWeight: 500, textDecoration: 'underline' }}>
                  {date}
                </Text>
              </View>
            </View>
          </View>
        </View>
      </Page>
    </Document>
  );
};
